import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { configFileKey } from "../git.slice";

import getGitBlobContent from "./getGitBlobContent.thunk";
import getGitCommit from "./getGitCommit.thunk";
import getGitTree from "./getGitTree.thunk";

import type { Blob } from "@packages/client";

export type LoadConfigFileParam = {
  projectId?: string;
  envHeadCommit?: string;
  /** Config directory name from `CUSTOM_CONFIG_DIR_NAME` (e.g. `.upsun`). */
  configFolder: string;
};

export type ConfigFile = {
  /** Display path of the resolved file, e.g. `.upsun/config.yaml`. */
  path: string;
  /** Raw file text; undefined when the file is absent in the tree. */
  content?: string;
};

/**
 * Resolves an environment's single-file config (`<configDir>/config.yaml`) and
 * returns its raw text — rendered as-is, like the apps/services Configuration
 * tab (no client-side YAML serialization).
 *
 * First consumer is the task detail YAML tab: tasks are an Upsun feature with
 * no per-task config file, so the tab shows the config.yaml that declares them.
 * This deliberately ignores the deployment services map (which
 * `useGetServiceAppBlob` keys off): a task name can collide with a service name
 * (see adr/0002), and resolving via services would render the wrong file.
 *
 * Orchestrates the existing git thunks (commit → tree → blob) so the fetched
 * commit/tree/blob stay cached in the `git` slice.
 */
export const loadConfigFile = createAppAsyncThunk(
  "git/loadConfigFile",
  async (
    { projectId, envHeadCommit, configFolder }: LoadConfigFileParam,
    { dispatch }
  ): Promise<ConfigFile> => {
    const path = configFolder.concat("/config.yaml");

    if (!projectId || !envHeadCommit) {
      return { path };
    }

    const commit = await dispatch(
      getGitCommit({ projectId, envHeadCommit })
    ).unwrap();

    let commitId = commit?.tree;
    let blob: Blob | undefined;

    // Descend the path segments (e.g. [".upsun", "config.yaml"]), following
    // each directory tree until the config.yaml blob is reached.
    for (const segment of path.split("/")) {
      if (!commitId) break;

      const tree = await dispatch(getGitTree({ projectId, commitId })).unwrap();
      const entry = tree?.tree.find(node => node?.path === segment);

      if (entry?.type === "tree") {
        commitId = entry.sha;
        continue;
      }
      if (entry?.type === "blob") {
        blob = entry;
      }
      break;
    }

    const content = blob
      ? await dispatch(getGitBlobContent(blob)).unwrap()
      : undefined;

    return { path, content };
  },
  {
    // Idempotent: skip when a load is in flight or the file already resolved.
    // No pending/fulfilled dispatches when skipped, so the cached entry — and
    // the component re-dispatching on every mount — stays a cheap no-op.
    condition: ({ projectId, envHeadCommit }, { getState }) => {
      const entry =
        getState().git?.configFile?.[configFileKey(projectId, envHeadCommit)];

      return !entry?.isLoading && !entry?.data;
    }
  }
);

export default loadConfigFile;
